/**
 * Utilities for keyboard shortcuts.
 *
 * Native re-implementation of the keyboard helper that used to be built on
 * `joint.ui.Keyboard` (Rappid). The public API and shortcut grammar are kept
 * identical so the call sites (Modal, ProjectMenu, ProjectVersion) are
 * unchanged:
 *
 *   - A shortcut is `[modifier+]value` where value is a single char or one of
 *     the named keys below (`enter`, `esc`, `del`, `F1`, ...). Modifiers are
 *     `shift`, `ctrl`/`control`, `alt`/`option`, `meta`/`command`.
 *   - Whitespace inside a shortcut separates alternatives bound to the SAME
 *     handler: `'F1 h'` fires on either F1 OR h (this matches Rappid, where the
 *     space-joined hash list was handed to Backbone.Events.on, which splits on
 *     spaces and binds the callback to each event).
 *   - Matching is by keyCode and exact modifier state, on `keydown`.
 *   - Keystrokes originating from <input>, <select>, <textarea> or a
 *     contentEditable element are ignored (Rappid's isUnsupportedElement).
 *   - Tracking is enabled by default; disable()/enable() toggle the global
 *     document listener.
 */

// The modifier flags of a KeyboardEvent we match against.
type ModifierKey = 'shiftKey' | 'ctrlKey' | 'altKey' | 'metaKey';

// A parsed matcher: the keyCode (`which`) plus the exact modifier state.
type Descriptor = { which: number } & Record<ModifierKey, boolean>;

// A registered (shortcut, handler) pair plus its parsed alternatives.
interface Binding {
  shortcut: string;
  handler: (event: KeyboardEvent) => void;
  alternatives: Descriptor[];
}

// Named-key -> keyCode map (mirrors Rappid's KeyboardParser keyMap).
const KEY_CODES: Record<string, number> = {
  backspace: 8,
  tab: 9,
  shift: 16,
  ctrl: 17,
  alt: 18,
  meta: 91,
  clear: 12,
  enter: 13,
  return: 13,
  esc: 27,
  escape: 27,
  capslock: 20,
  space: 32,
  left: 37,
  up: 38,
  right: 39,
  down: 40,
  del: 46,
  delete: 46,
  home: 36,
  end: 35,
  insert: 45,
  ins: 45,
  pageup: 33,
  pagedown: 34,
  plus: 187,
  minus: 189,
  '-': 189,
  ',': 188,
  '.': 190,
  '/': 191,
  '`': 192,
  '=': 187,
  ';': 186,
  "'": 222,
  '[': 219,
  ']': 221,
  '\\': 220,
  F1: 112,
  F2: 113,
  F3: 114,
  F4: 115,
  F5: 116,
  F6: 117,
  F7: 118,
  F8: 119,
  F9: 120,
  F10: 121,
  F11: 122,
  F12: 123,
};

const MODIFIERS: Record<string, ModifierKey> = {
  '⇧': 'shiftKey',
  shift: 'shiftKey',
  '⌥': 'altKey',
  alt: 'altKey',
  option: 'altKey',
  '⌃': 'ctrlKey',
  ctrl: 'ctrlKey',
  control: 'ctrlKey',
  '⌘': 'metaKey',
  command: 'metaKey',
  meta: 'metaKey',
};

function keyCode(value: string): number {
  if (Object.prototype.hasOwnProperty.call(KEY_CODES, value)) {
    return KEY_CODES[value];
  }
  return value.toUpperCase().charCodeAt(0);
}

// Parse one alternative (e.g. `shift+e`) into a matcher descriptor.
function parseAlternative(raw: string): Descriptor {
  const parts = raw
    .replace(/\s*\+\s*/g, '+')
    .split('+')
    .filter(Boolean);
  const descriptor: Descriptor = {
    which: 0,
    shiftKey: false,
    ctrlKey: false,
    altKey: false,
    metaKey: false,
  };
  for (const part of parts) {
    const modifier = MODIFIERS[part];
    if (modifier) {
      descriptor[modifier] = true;
    }
    if (parts.length === 1 || !modifier) {
      descriptor.which = keyCode(part);
    }
  }
  return descriptor;
}

// A shortcut string may contain space-separated alternatives bound to one
// handler. Returns a list of matcher descriptors.
function parseShortcut(shortcut: string): Descriptor[] {
  return shortcut
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(parseAlternative);
}

function matches(descriptor: Descriptor, event: KeyboardEvent): boolean {
  return (
    event.keyCode === descriptor.which &&
    event.shiftKey === descriptor.shiftKey &&
    event.ctrlKey === descriptor.ctrlKey &&
    event.altKey === descriptor.altKey &&
    event.metaKey === descriptor.metaKey
  );
}

function isUnsupportedElement(event: KeyboardEvent): boolean {
  // `Event.target`/`srcElement` are typed `EventTarget | null`, which lacks the
  // HTMLElement members read below; the keydown source is an element at runtime.
  const element = (event.target || event.srcElement) as HTMLElement | null;
  if (!element || !element.tagName) {
    return false;
  }
  const tagName = element.tagName.toUpperCase();
  return (
    tagName === 'INPUT' ||
    tagName === 'SELECT' ||
    tagName === 'TEXTAREA' ||
    element.isContentEditable
  );
}

// Registered bindings: each entry pairs the original (shortcut, handler) with
// its parsed alternatives, so removeListener can match by reference exactly
// like Backbone.Events.off did.
const bindings: Binding[] = [];
let enabled = false;

function handleKey(event: KeyboardEvent): void {
  if (isUnsupportedElement(event)) {
    return;
  }
  // Snapshot to tolerate handlers that add/remove listeners while firing.
  for (const binding of bindings.slice()) {
    if (binding.alternatives.some((descriptor) => matches(descriptor, event))) {
      binding.handler(event);
    }
  }
}

function enable(): void {
  if (enabled || typeof document === 'undefined') {
    return;
  }
  document.addEventListener('keydown', handleKey, false);
  enabled = true;
}

function disable(): void {
  if (!enabled || typeof document === 'undefined') {
    return;
  }
  document.removeEventListener('keydown', handleKey, false);
  enabled = false;
}

// Enabled by default, matching the old `new joint.ui.Keyboard()` constructor.
enable();

export const keyboard = {
  addListener(shortcut: string, handler: (event: KeyboardEvent) => void) {
    bindings.push({ shortcut, handler, alternatives: parseShortcut(shortcut) });
  },
  removeListener(shortcut: string, handler: (event: KeyboardEvent) => void) {
    for (let i = bindings.length - 1; i >= 0; i--) {
      if (bindings[i].shortcut === shortcut && bindings[i].handler === handler) {
        bindings.splice(i, 1);
      }
    }
  },
  enable,
  disable,
};
